import { useState } from "react";
import { toast } from "sonner";
import { Banknote, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Panel, KV } from "@/components/admin/Panel";
import { api, formatErr, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SetupLink = ({ value, email, onClose }) => (
  <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="setup-link-dialog">
      <DialogHeader>
        <DialogTitle>Password setup link emailed</DialogTitle>
        <DialogDescription>We emailed {email} a single-use link (valid 7 days) to create their portal password. Backup copy below if they can't find it.</DialogDescription>
      </DialogHeader>
      <div className="rounded-xl bg-secondary p-4 text-xs font-mono break-all" data-testid="setup-link-value">{value}</div>
      <button onClick={() => { navigator.clipboard?.writeText(value); toast.success("Link copied"); }} className="h-10 rounded-full border border-border text-sm font-semibold hover:bg-secondary" data-testid="setup-link-copy-button">Copy link</button>
    </DialogContent>
  </Dialog>
);

export const PaymentPanel = ({ app, defaultAmount, onSaved }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ amount: defaultAmount || "", date: new Date().toISOString().slice(0, 10), received_by: user?.email || "" });
  const [temp, setTemp] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const { data } = await api.post(`/admin/applications/${app.id}/payment`, { ...form, amount: Number(form.amount) });
      setTemp(data.setup_link);
      onSaved(data);
      toast.success("Cash payment recorded - membership activated");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const reset = async () => {
    try {
      const { data } = await api.post(`/admin/applications/${app.id}/reset-password`);
      setTemp(data.setup_link);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const p = app.payment;
  return (
    <Panel title="Cash payment" testId="panel-payment">
      <SetupLink value={temp} email={app.contact.email} onClose={() => setTemp("")} />
      {p ? (
        <div className="space-y-4">
          <KV rows={[["Amount", money(p.amount)], ["Date", p.date], ["Received by", p.received_by], ["Recorded by", p.recorded_by]]} />
          <button onClick={reset} className="h-10 px-4 rounded-full border border-border text-sm font-semibold inline-flex items-center gap-2 hover:bg-secondary" data-testid="member-reset-password-button"><KeyRound className="h-4 w-4" />Email new password link</button>
        </div>
      ) : app.status !== "Approved" ? (
        <p className="text-sm text-muted-foreground" data-testid="payment-locked-note">Available once the applicant is Approved.</p>
      ) : (
        <div className="space-y-3">
          <Input type="number" placeholder="Amount (USD)" value={form.amount} onChange={set("amount")} className="h-11 rounded-xl" data-testid="payment-amount-input" />
          <Input type="date" value={form.date} onChange={set("date")} className="h-11 rounded-xl" data-testid="payment-date-input" />
          <Input placeholder="Received by" value={form.received_by} onChange={set("received_by")} className="h-11 rounded-xl" data-testid="payment-received-by-input" />
          <button onClick={save} className="w-full h-11 rounded-full bg-amber-500 text-[#0A1F13] font-bold inline-flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors" data-testid="mark-cash-payment-button">
            <Banknote className="h-4 w-4" /> Mark Cash Payment Received
          </button>
        </div>
      )}
    </Panel>
  );
};
